import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as queryString from 'query-string';
import PropTypes from 'prop-types';

import withAuth from '../../../components/hocs/withAuth';
import withStatsig from '../../../components/hocs/withStatsig';
import SignInForm from '../../../components/SignIn';
import { screens } from '../../../components/PopUps/LoginPopUpWrapper/LoginPopUp/utils';
import Img from '../../../components/Img';
import { PopupTypes } from '../../../utils/constants';

import { signInEmail } from '../../../actions';
import * as popupActions from '../../../actions/popup-actions';
import { connectThirdPartyId } from '../../../actions/activate-utr-actions';
import { getRedirectUrl, getUserAge, isUtrActivationInProgress, shouldCallConnectPlayerThirdPartyId, shouldNavigateToRedirectUrl, UtrActivationUnderAgeLimit } from '../../../utils/activateUtr';
import { resolveThirdPartyRedirect } from '../../../utils/loginRedirect';

import config from '../../../../config';

import styles from './login.scss';
import TennisAustraliaLogin from '../../../components/TennisAustraliaLogin';
import AppleLoginV3 from '../../../components/AppleLoginV3';
import FacebookLoginV2 from '../../../components/FacebookLoginV2';
import MagicLinkLoginFlow from '../MagicLinkLoginFlow';

class Login extends Component {
    constructor(props) {
        super(props);
        this.submit = this.submit.bind(this);
        this.joinNow = this.joinNow.bind(this);
        if (props?.auth?.user) {
            if (props?.inPopUp) {
                props.moveToNextScreen();
            } else {
                const parsed = queryString.parse(this.props.location.search);
                // tpr (third-party redirect) routes the user to a partner destination after
                // auth — same-tab via window.location.href because we're outside a click
                // gesture and `window.open(..., '_blank')` would be popup-blocked.
                // Sits just above `redirect`; `next` wins.
                const tprUrl = resolveThirdPartyRedirect(parsed.tpr, props.auth.user);
                if (parsed.next) {
                    props.history.replace(parsed.next);
                } else if (tprUrl) {
                    window.location.href = tprUrl;
                } else if (parsed.redirect) {
                    window.open(parsed.redirect, '_self');
                } else {
                    props.history.replace('/home'); // Go to dashboard if logged in
                }
            }
        }
        this.state = {
            submitting: false,
        };
    }

    async componentDidUpdate() {
        if (this.props.auth.user) {
            const { mfaOptions } = this.props.auth.user;
            if (mfaOptions?.mfaRequired) {
                if (mfaOptions?.mfaEnabled) {
                    this.props.history.push('/mfa');
                } else {
                    this.props.history.push('/mfa-setup');
                }
            } else if (!this.props.inPopUp) {
                const parsed = queryString.parse(this.props.location.search);
                const {
                    onboardingRequired,
                    onboardingCompleted,
                } = this.props.auth.user;

                const utrActivationInProgress = isUtrActivationInProgress(this.props.activateUtr);

                if (onboardingRequired && !onboardingCompleted) {
                    let queryParams = {};
                    if (utrActivationInProgress) {
                        queryParams = {
                            ...parsed,
                            activationType: this.props.activateUtr.activationType,
                            activationToken: this.props.activateUtr.activationToken,
                        };
                    } else {
                        queryParams = { ...parsed };
                        if (parsed.next && !parsed.return) {
                            queryParams.return = parsed.next;
                            delete queryParams.next;
                        }
                    }
                    const url = `/onboarding${Object.keys(queryParams).length > 0 ? `?${queryString.stringify(queryParams, { encode: true })}` : ''}`;
                    this.props.history.replace(url);

                    return;
                }

                if (utrActivationInProgress) {
                    if (shouldCallConnectPlayerThirdPartyId(this.props.activateUtr)) {
                        await this.props.connectPlayerThirdPartyId({
                            type: this.props.activateUtr.activationType,
                            token: this.props.activateUtr.activationToken,
                        });
                    }

                    if (!shouldNavigateToRedirectUrl(this.props.activateUtr)) {
                        return;
                    }
                }

                // tpr (third-party redirect) routes the user to a partner destination after
                // auth — same-tab via window.location.href because we're outside a click
                // gesture and `window.open(..., '_blank')` would be popup-blocked.
                // Sits just above `redirect`: activateUtr / next / upgradeAndRedirect win.
                const tprUrl = resolveThirdPartyRedirect(parsed.tpr, this.props.auth.user);
                if (shouldNavigateToRedirectUrl(this.props.activateUtr)) {
                    window.location.href = getRedirectUrl(this.props.activateUtr);
                } else if (parsed.next) {
                    this.props.history.replace(parsed.next);
                } else if (parsed.upgradeAndRedirect) {
                    // UT-8135: need to prompt for Power Upgrade and then redirect back to Insights
                    this.props.history.replace(parsed.upgradeAndRedirect);
                } else if (tprUrl) {
                    window.location.href = tprUrl;
                } else if (parsed.redirect) {
                    window.open(parsed.redirect, '_self');
                } else {
                    this.props.history.replace('/home');
                }
            } else {
                this.props.moveToNextScreen();
            }
        }
    }

    joinNow() {
        const { location, inPopUp, history, moveToScreen, showPopup, activateUtr } = this.props;
        if (inPopUp) {
            moveToScreen(screens.SIGN_UP);
        } else {
            const parsed = queryString.parse(location.search);
            const { next } = parsed;
            const joinParsed = parsed;
            if (next) {
                joinParsed.return = encodeURIComponent(next);
                delete joinParsed.next;
            }
            const joinUrl = `/join${Object.keys(joinParsed).length > 0 ? `?${queryString.stringify(joinParsed, { encode: false })}` : ''}`;

            if (isUtrActivationInProgress(activateUtr) && getUserAge(activateUtr) < UtrActivationUnderAgeLimit) {
                showPopup({
                    popup: PopupTypes.ACTIVATE_UTR_UNDER_AGE_POPUP,
                    onClosePopup: () => {
                        history.push(joinUrl);
                    },
                });
            } else {
                history.push(joinUrl);
            }
        }
    }

    async submit(values) {
        this.setState({
            submitting: true,
        });
        await this.props.signInEmail(values);
        this.setState({
            submitting: false,
        });
    }

    render() {
        const { auth, inPopUp, forceMobileUi, email, type, activateUtr, checkGate, featureGates } = this.props;
        const appleSignInEnabled = checkGate(featureGates.APPLE_SIGN_IN);
        const appleOAuthV3Enabled = checkGate(featureGates.APPLE_OAUTH_V3);
        const facebookOAuthV2Enabled = checkGate(featureGates.FACEBOOK_OAUTH_V2);
        const magicLinkLoginEnabled = checkGate(featureGates.MAGIC_LINK_LOGIN);
        const parsed = queryString.parse(this.props.location.search);
        // Suppress the login form while componentDidUpdate is preparing a partner
        // redirect — window.location.href is a hard nav and React will otherwise
        // render once more before the browser leaves the page, flashing the form.
        // Higher-priority paths (MFA, Onboarding, next, upgradeAndRedirect) all
        // SPA-swap and don't flash, so leave the form visible when any of them
        // will fire instead of tpr.
        const mfaWillIntercept = !!auth.user?.mfaOptions?.mfaRequired;
        const onboardingWillIntercept = !!auth.user?.onboardingRequired && !auth.user?.onboardingCompleted;
        if (
            auth.user &&
            !inPopUp &&
            !mfaWillIntercept &&
            !onboardingWillIntercept &&
            !parsed.next &&
            !parsed.upgradeAndRedirect &&
            resolveThirdPartyRedirect(parsed.tpr, auth.user)
        ) {
            return null;
        }
        let sourcePath = null;
        if (inPopUp) {
            parsed.showPopup = true;
            parsed.type = type;
            const queries = queryString.stringify(parsed);
            sourcePath = `${this.props.location.pathname}?${queries}`;
        } else {
            sourcePath = parsed.source;
        }
        const stateParam = Buffer.from(`{"sourcePath": "${sourcePath}"}`).toString('base64');

        const appId = '1701644616548664';
        const fbRedirectUri = `${config.appHost}/signin-facebook`;
        const facebookUrl = `https://www.facebook.com/v2.12/dialog/oauth?client_id=${appId}&redirect_uri=${fbRedirectUri}&state=${stateParam}&response_type=token&scope=email`;
        const appleRedirectURL = `${config.apiHost}/v2/auth/appleredirect`;
        const appleURL = `https://appleid.apple.com/auth/authorize?response_type=code+id_token&response_mode=form_post&client_id=${config.appleClientId}&redirect_uri=${appleRedirectURL}&scope=name+email`;
        const appleOAuthV3URL = `${config.apiHost}/v3/oauth/apple/initiate?platform=web&flow=login`;
        const facebookOAuthV2URL = `${config.apiHost}/v3/oauth/facebook/initiate?platform=web&flow=login`;

        const appleAuthURL = appleOAuthV3Enabled ? appleOAuthV3URL : appleURL;
        const facebookAuthURL = facebookOAuthV2Enabled ? facebookOAuthV2URL : facebookUrl;

        let parsedTitle = '';

        const renderAppleAuthButton = () => {
            return (
                <a
                    className={`btn btn-primary btn-block mb16 btn-xl`}
                    href={appleURL}
                    data-testid="login.page.apple-btn"
                >
                    <div className={`d-flex jcc align-items-center ${styles.appleLogin}`}>
                        <i className="fab fa-apple" />
                        <div className="ml-2 text-uppercase" data-testid="login.page.apple-text">Sign In with Apple</div>
                    </div>
                </a>
            );
        };

        switch (parsed.flow) {
            case 'search':
                parsedTitle = 'Log in to view search results';
                break;
            case 'club_create':
                parsedTitle = 'Log in to create your UTR Event';
                break;
            case 'high_school_club_create':
                parsedTitle = 'Log in to complete your UTR High School Club';
                break;
            default:
                parsedTitle = 'Sign In';
                break;
        }
        const title = this.props.title || parsedTitle;

        const utrActivationInProgress = isUtrActivationInProgress(activateUtr);
        const defaultEmail = email || (utrActivationInProgress ? activateUtr?.user?.email : '');

        return (
            <div className={`${styles.section} ${forceMobileUi ? styles.popUp : ''}`}>
                <div className={`container`}>
                    <div className="row">
                        <div className={`${forceMobileUi ? 'col' : 'col-12 col-md-6 offset-md-3'}`}>
                            {magicLinkLoginEnabled ? (
                                <MagicLinkLoginFlow
                                    isSubmitting={this.state.submitting}
                                    onSignInWithCredentials={this.submit}
                                    onJoinNowClick={this.joinNow}
                                    facebookUrl={facebookAuthURL}
                                    appleUrl={appleAuthURL}
                                />
                            ) : (
                                <div className={`${styles.authCard}`}>
                                    {parsed.flow === 'club_create' && (
                                        <div className="step-header">
                                            <div className="text-highlight2" data-testid="login.page.step-indicator">STEP 5/6</div>
                                        </div>
                                    )}
                                    {parsed.flow === 'high_school_club_create' && (
                                        <div className="step-header">
                                            <div className="text-highlight2" data-testid="login.page.step-indicator">STEP 5/5</div>
                                        </div>
                                    )}
                                    {parsed.flow === 'tournament_club_create' && (
                                        <div className="step-header">
                                            <div className="text-highlight2" data-testid="login.page.step-indicator">STEP 5/5</div>
                                        </div>
                                    )}
                                    <h3 className={`${styles.title}`}>
                                        <span data-testid="login.page.title">{title}</span>
                                    </h3>
                                    {auth.error && (
                                        <p className={styles.errorText} data-testid="login.page.error-text">
                                            {auth.error}
                                        </p>
                                    )}
                                    <SignInForm onSubmit={this.submit} email={defaultEmail} submitting={this.state.submitting} isSignInFlow={parsedTitle === 'Sign In'} data-testid="login.page.sign-in-form" />
                                    <div className={`${styles.tos} mb-1`}>
                                        <span data-testid="login.page.tos">By continuing, I agree to the <a href={`${config.marketingAppHost}/tos`} target="_blank" rel="noopener noreferrer" data-testid="login.page.tos-link">Terms of service</a>.</span>
                                    </div>
                                    <a className={`${styles.forgotLink}`} href={`/forgotpassword`} data-testid="login.page.forgot-password-link">Forgot Password?</a>
                                    <hr className="hr-text" data-content="or" />
                                    {!utrActivationInProgress && (
                                    <>
                                        {facebookOAuthV2Enabled ? (
                                            <FacebookLoginV2 flow="login" buttonText="Sign In with Facebook" data-testid="login.page.facebook-btn" />
                                        ) : (
                                            <a
                                                className={`btn btn-primary btn-block mb16 btn-xl`}
                                                href={facebookUrl}
                                                data-testid="login.page.facebook-btn"
                                            >
                                                <div className="d-flex jcc">
                                                    <Img src="/img/social/facebook-icon.svg" data-testid="login.page.facebook-icon" />
                                                    <div className="ml-2 text-uppercase" data-testid="login.page.facebook-text">Sign In with Facebook</div>
                                                </div>
                                            </a>
                                        )}
                                    </>
                                    )}
                                    {appleOAuthV3Enabled ? (
                                        <AppleLoginV3 flow="login" buttonText="Sign In with Apple" data-testid="login.page.apple-btn" />
                                    ) : appleSignInEnabled && renderAppleAuthButton()}
                                    <TennisAustraliaLogin data-testid="login.page.tennis-australia-btn" />
                                    <div className={`${styles.divider}`} />
                                    <div className={`${styles.text}`} data-testid="login.page.no-account-text">
                                        <strong>Don&apos;t have an account?</strong> <span><a className="cursor-pointer text-teal" onClick={this.joinNow} data-testid="login.page.join-now-link">Join now.</a></span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

function mapDispatchToProps(dispatch) {
    return {
        signInEmail: (data) => dispatch(signInEmail(data)),
        connectPlayerThirdPartyId: (data) => dispatch(connectThirdPartyId(data)),
        showPopup: (popUpData) => {
            dispatch(popupActions.showPopupNew(popUpData));
        },
    };
}

function mapStateToProps({ activateUtr }) {
    return { activateUtr };
}

Login.propTypes = {
    auth: PropTypes.objectOf(PropTypes.any).isRequired,
    checkGate: PropTypes.func.isRequired,
    featureGates: PropTypes.shape({
        APPLE_SIGN_IN: PropTypes.string,
    }).isRequired,
};

export default withStatsig(withAuth(connect(mapStateToProps, mapDispatchToProps)(Login)));
